/**
 * Meeting Notes Service
 * Single Responsibility: Handle meeting notes business logic
 * Dependency Inversion: Depends on interfaces, not implementations
 */

import { Task, LifeEvent } from '../types/meeting'
import { postMeetingApi } from './api/postMeetingApi'
import {
  isValidSessionId,
  sanitizeClientName,
  getValidatedClientType,
  ClientType
} from '../utils/validation'

// The backend leaves priority/status/financial_impact as free strings — narrow
// them to the frontend unions here, falling back rather than trusting the wire.
const TASK_PRIORITIES = ['low', 'medium', 'high'] as const
const TASK_STATUSES = ['pending', 'in-progress', 'completed'] as const
const FINANCIAL_IMPACTS = ['high', 'medium', 'low', 'none'] as const

function narrowEnum<T extends string, F extends T | undefined>(
  value: string | null | undefined,
  allowed: readonly T[],
  fallback: F,
): T | F {
  return allowed.includes(value as T) ? (value as T) : fallback
}

// Interface Segregation: Separate interfaces for different concerns
export interface MeetingNotesRequest {
  sessionId: string
  clientType?: ClientType
  clientName?: string
  sessionData?: any
}

export interface MeetingNotesResult {
  success: boolean
  summary?: string
  tasks?: Task[]
  lifeEvents?: LifeEvent[]  // Added life events field
  finsaStatus?: 'advisory_minutes' | 'business_notes' | 'no_documentation'
  requiresClientDelivery?: boolean
  // Surfaced from FINSA notes' keyInsights when available. Only populated on
  // the FINSA fallback branch today; extracted-items branch leaves these
  // undefined (the upstream extracted-items endpoint does not return them).
  clientMood?: string
  keyPoints?: string[]
  error?: {
    code: string
    message: string
    retryable: boolean
  }
}

export interface TaskExtractor {
  extractTasks(insights: any): Task[]
}

/**
 * Service for generating tasks from insights
 * Single Responsibility: Task extraction logic
 */
export class TaskExtractionService implements TaskExtractor {
  extractTasks(insights: any): Task[] {
    if (!insights?.nextSteps || !Array.isArray(insights.nextSteps)) {
      return []
    }
    
    return insights.nextSteps.map((step: string, index: number) => ({
      id: `task-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
      title: step,
      owner: this.determineOwner(step),
      dueDate: this.calculateDueDate(step),
      priority: this.determinePriority(step),
      status: 'pending' as const
    }))
  }
  
  private determineOwner(taskTitle: string): string {
    // Logic to determine owner based on task content
    const lowerTitle = taskTitle.toLowerCase()
    
    if (lowerTitle.includes('client') || lowerTitle.includes('customer')) {
      return 'Client'
    }
    if (lowerTitle.includes('compliance') || lowerTitle.includes('regulatory')) {
      return 'Compliance Team'
    }
    if (lowerTitle.includes('portfolio') || lowerTitle.includes('investment')) {
      return 'Investment Team'
    }
    
    return 'Advisor' // Default to advisor
  }
  
  private calculateDueDate(taskTitle: string): string {
    const lowerTitle = taskTitle.toLowerCase()
    const now = Date.now()
    let daysToAdd = 7 // Default: 1 week
    
    // Urgent tasks
    if (lowerTitle.includes('urgent') || lowerTitle.includes('immediate')) {
      daysToAdd = 1
    }
    // Compliance tasks
    else if (lowerTitle.includes('compliance') || lowerTitle.includes('regulatory')) {
      daysToAdd = 3
    }
    // Follow-up tasks
    else if (lowerTitle.includes('follow-up') || lowerTitle.includes('follow up')) {
      daysToAdd = 14
    }
    // Quarterly tasks
    else if (lowerTitle.includes('quarterly') || lowerTitle.includes('quarter')) {
      daysToAdd = 90
    }
    
    return new Date(now + daysToAdd * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  }
  
  private determinePriority(taskTitle: string): 'low' | 'medium' | 'high' {
    const lowerTitle = taskTitle.toLowerCase()
    
    if (lowerTitle.includes('urgent') || 
        lowerTitle.includes('critical') || 
        lowerTitle.includes('compliance') ||
        lowerTitle.includes('regulatory')) {
      return 'high'
    }
    
    if (lowerTitle.includes('review') || 
        lowerTitle.includes('analysis') ||
        lowerTitle.includes('report')) {
      return 'medium'
    }
    
    return 'low'
  }
}

/**
 * Main Meeting Notes Service
 * Open/Closed: Open for extension through dependency injection
 */
export class MeetingNotesService {
  constructor(
    private taskExtractor: TaskExtractor = new TaskExtractionService()
  ) {}
  
  /**
   * Generate meeting notes with proper validation and error handling
   */
  async generateMeetingNotes(request: MeetingNotesRequest): Promise<MeetingNotesResult> {
    // Validate session ID
    if (!isValidSessionId(request.sessionId)) {
      return {
        success: false,
        error: {
          code: 'INVALID_SESSION_ID',
          message: 'Invalid meeting session. Please return to the dashboard and try again.',
          retryable: false
        }
      }
    }
    
    // Sanitize and validate inputs
    const sanitizedClientName = sanitizeClientName(request.clientName)
    const validatedClientType = request.clientType || getValidatedClientType(request.sessionData)
    
    try {
      // Try to fetch all extracted items first
      let tasks: Task[] = []
      let lifeEvents: LifeEvent[] = []
      let summary = ''
      let finsaStatus: 'advisory_minutes' | 'business_notes' | 'no_documentation' = 'no_documentation'
      let requiresClientDelivery = false
      let clientMood: string | undefined
      let keyPoints: string[] | undefined

      try {
        // Fetch all extracted items (tasks and life events) in one call
        const extractedItems = await postMeetingApi.getExtractedItems(request.sessionId)

        // Check if we actually got meaningful data.
        // TASK-423: `summary` is NOT a reliable signal — the backend always
        // generates a summary (via fallback LLM) even when extraction hasn't
        // completed. Previously this short-circuited the frontend into using
        // empty tasks/life-events arrays. Now require at least one of tasks
        // or life-events to be present; if both are empty, fall through to
        // the explicit `generateFINSANotes` + `generateTasks` path which
        // re-runs extraction.
        const hasExtractedData = extractedItems.success &&
          ((extractedItems.tasks && extractedItems.tasks.length > 0) ||
           (extractedItems.life_events && extractedItems.life_events.length > 0))

        if (hasExtractedData) {
          // Map backend tasks to frontend Task format.
          // Backend `TaskResponse` shape (post_meeting_routes_with_tasks.py:143):
          //   { id, task_description, assigned_to, due_date, priority, status,
          //     context: { description?: string }, created_at }
          // The DB `title` column is aliased to `task_description` in the SQL
          // SELECT, and the DB `description` column is nested under `context`.
          // We display `task_description` as the primary title (falling back to
          // `title` only for legacy responses) and `context.description` as the
          // detail text.
          tasks = extractedItems.tasks.map((task) => {
            const ctx = task.context || {}
            return {
              id: task.id,
              title: task.task_description || task.title || '',
              description: (ctx.description as string | undefined) || '',
              owner: task.assigned_to || 'advisor',
              priority: narrowEnum(task.priority, TASK_PRIORITIES, 'medium'),
              status: narrowEnum(task.status, TASK_STATUSES, 'pending'),
              dueDate: task.due_date || null,
            }
          })

          // Map backend life events to frontend LifeEvent format.
          // Backend `LifeEventResponse` shape (post_meeting_routes_with_tasks.py:158):
          //   { id, event_type, event_date, description, financial_impact,
          //     priority, context, created_at }
          // The frontend previously read `event.date` (wrong key) and
          // `event.confidence_score`/`event.source` which the backend does not
          // expose on this endpoint.
          lifeEvents = extractedItems.life_events.map((event) => ({
            id: event.id,
            date: event.event_date || '',
            type: (event.event_type || 'other') as LifeEvent['type'],
            description: event.description || '',
            financialImpact: narrowEnum(event.financial_impact, FINANCIAL_IMPACTS, undefined),
            source: 'transcription' as LifeEvent['source'],
            timestamp: event.created_at || new Date().toISOString(),
          }))

          summary = extractedItems.summary || ''
          finsaStatus = extractedItems.finsa_status || 'no_documentation'
          requiresClientDelivery = extractedItems.requires_delivery || false
        } else {
          // No meaningful extracted data, throw to trigger fallback
          throw new Error('No extracted data available, falling back to FINSA notes')
        }
      } catch (extractionError) {
        console.log('Extracted items endpoint failed, falling back to FINSA notes:', extractionError)

        // Fall back to original FINSA notes endpoint
        const notesResponse = await postMeetingApi.generateFINSANotes({
          sessionId: request.sessionId,
          clientType: validatedClientType,
          clientName: sanitizedClientName,
          includeEmailDraft: false
        })

        summary = notesResponse.notes?.executiveSummary ||
                 notesResponse.notes?.businessNotes ||
                 ''

        finsaStatus = notesResponse.documentationType
        requiresClientDelivery = notesResponse.requiresClientDelivery
        clientMood = notesResponse.notes?.keyInsights?.clientMood
        keyPoints = notesResponse.notes?.keyInsights?.keyPoints

        // Try to fetch tasks separately
        try {
          const tasksResponse = await postMeetingApi.generateTasks(request.sessionId)
          if (tasksResponse.success && tasksResponse.tasks) {
            tasks = tasksResponse.tasks
          }
        } catch (taskError) {
          console.log('Task generation endpoint failed, extracting from insights:', taskError)
          tasks = this.taskExtractor.extractTasks(notesResponse.notes?.keyInsights)
        }

        // Life events will be empty in fallback mode
        lifeEvents = []
      }

      return {
        success: true,
        summary,
        tasks,
        lifeEvents, // Now including life events
        finsaStatus,
        requiresClientDelivery,
        clientMood,
        keyPoints
      }
      
    } catch (error) {
      return this.handleError(error)
    }
  }
  
  /**
   * Handle errors with specific messages and codes
   */
  private handleError(error: any): MeetingNotesResult {
    // Type-safe error handling
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
    const statusCode = error?.response?.status
    
    // Specific error handling based on status code
    if (statusCode === 404) {
      return {
        success: false,
        error: {
          code: 'MEETING_NOT_FOUND',
          message: 'Meeting session not found. It may have expired or been deleted.',
          retryable: false
        }
      }
    }
    
    if (statusCode === 401 || statusCode === 403) {
      return {
        success: false,
        error: {
          code: 'AUTHENTICATION_REQUIRED',
          message: 'Your session has expired. Please log in again.',
          retryable: false
        }
      }
    }
    
    if (statusCode === 429) {
      return {
        success: false,
        error: {
          code: 'RATE_LIMITED',
          message: 'Too many requests. Please wait a moment and try again.',
          retryable: true
        }
      }
    }
    
    if (statusCode === 500 || statusCode === 502 || statusCode === 503) {
      return {
        success: false,
        error: {
          code: 'SERVER_ERROR',
          message: 'Our servers are experiencing issues. Please try again in a few moments.',
          retryable: true
        }
      }
    }
    
    // Network errors
    if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
      return {
        success: false,
        error: {
          code: 'TIMEOUT',
          message: 'The request took too long. Please check your connection and try again.',
          retryable: true
        }
      }
    }
    
    // Default error
    return {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: `Unable to generate meeting notes: ${errorMessage}`,
        retryable: true
      }
    }
  }
  
  /**
   * Regenerate summary for a meeting
   */
  async regenerateSummary(sessionId: string, sessionData?: any): Promise<MeetingNotesResult> {
    return this.generateMeetingNotes({
      sessionId,
      sessionData
    })
  }
}

// Export singleton instance for convenience
export const meetingNotesService = new MeetingNotesService()